import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { perfConfigSchema } from '@1qa/shared';
import { Copy, FileText, Gauge, Play, Plus, Trash2 } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import { describeLoad } from '@/lib/perf';
import type { Project, TestCase } from '@/lib/types';
import { Button, Card, EmptyState, LinkButton, useConfirm, useToast } from '@/components/ui';

/**
 * The performance tests of a performance project: each a load plan over the project's scripts. A new project starts
 * with a script (Scripts tab), then a test that runs it.
 */
export function PerfTestsTab({ project, tests }: { project: Project; tests: TestCase[] }) {
  const { can, canModify } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const scripts = useQuery({ queryKey: ['perf-scripts', project.id], queryFn: () => api.perfScripts(project.id) });
  const canWrite = can('test.write', project.orgId);
  const canRun = can('perf.run', project.orgId);
  const refresh = () => qc.invalidateQueries({ queryKey: ['tests', project.id] });
  const copy = useMutation({ mutationFn: (id: string) => api.copyTest(id), onSuccess: () => (toast.success('Copied'), refresh()), onError: (e) => toast.error(errorMessage(e)) });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteTest(id), onSuccess: () => (toast.success('Performance test deleted'), refresh()), onError: (e) => toast.error(errorMessage(e)) });
  const noScripts = scripts.isSuccess && scripts.data.length === 0;

  if (tests.length === 0) {
    return (
      <EmptyState
        icon={<Gauge className="size-6" />}
        title="No performance tests yet"
        description={
          noScripts
            ? 'Start with a script: one user’s journey, recorded with the 1QA Recorder or uploaded as a HAR file. A performance test then runs scripts for many users at once.'
            : 'A performance test runs the project’s scripts for many users at once: choose the scripts, how the load builds up, and the goals.'
        }
        action={
          canWrite &&
          (noScripts ? (
            <LinkButton to={`/projects/${project.id}/perf/scripts/new`} state={{ record: true }} icon={<FileText className="size-4" />}>
              Make a script
            </LinkButton>
          ) : (
            <LinkButton to={`/projects/${project.id}/perf/new`} icon={<Plus className="size-4" />}>
              New performance test
            </LinkButton>
          ))
        }
      />
    );
  }

  return (
    <Card>
      {canWrite && (
        <div className="flex flex-wrap items-center justify-end gap-3 border-b border-border-subtle px-5 py-3.5">
          <LinkButton to={`/projects/${project.id}/perf/new`} variant="outline" icon={<Plus className="size-4" />}>
            New performance test
          </LinkButton>
        </div>
      )}
      <ul className="divide-y divide-border-subtle" data-testid="perf-tests">
        {tests.map((t) => {
          const plan = perfConfigSchema.safeParse(t.perfConfig).data;
          const href = `/projects/${project.id}/perf/${t.id}`;
          return (
            <li key={t.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary-subtle text-primary">
                <Gauge className="size-4" />
              </span>
              <Link to={href} className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-fg hover:text-primary">{t.name}</div>
                <div className="truncate text-xs text-fg-muted">
                  {plan ? `${plan.scenarios.length} scenario${plan.scenarios.length === 1 ? '' : 's'} · ${describeLoad(plan.load)}` : 'No valid plan'} · updated {timeAgo(t.updatedAt)}
                  {t.createdBy ? ` by ${t.createdBy.name}` : ''}
                </div>
              </Link>
              {canRun && (
                <LinkButton to={`${href}?run=1`} variant="ghost" size="sm" aria-label={`Run ${t.name}`} icon={<Play className="size-4" />}>
                  Run
                </LinkButton>
              )}
              {canWrite && <Button variant="ghost" size="sm" aria-label={`Copy ${t.name}`} onClick={() => copy.mutate(t.id)} icon={<Copy className="size-4" />} />}
              {canModify('test.write', t.createdById, project.orgId) && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Delete ${t.name}`}
                  onClick={async () => {
                    if (await confirm({ title: 'Delete this performance test?', message: `“${t.name}” and its run reports will be deleted. Its scripts stay in the library.`, confirmLabel: 'Delete' })) remove.mutate(t.id);
                  }}
                  icon={<Trash2 className="size-4" />}
                />
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
